import { useId, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { LoadingButton } from '@/components/loading-button'
import { useRecordHandBooking } from '@/api/hooks'
import { formatApiErrorMessage } from '@/lib/api-error'

interface HandBookingFormProps {
  poUuid: string
  shipmentId: number
  onDone: () => void
}

/**
 * Freight booked by hand in the WWEX portal (or on UPS.com): the BOL is enough, WWEX supplies the
 * carrier, PRO and pickup number. Recording it settles the failed booking (USA-1455).
 */
export function HandBookingForm({ poUuid, shipmentId, onDone }: HandBookingFormProps) {
  const id = useId()
  const recordBooking = useRecordHandBooking(poUuid)
  const [reference, setReference] = useState('')

  return (
    <form
      className="flex flex-col gap-2 sm:flex-row sm:items-end"
      aria-label="Booked by hand"
      onSubmit={(event) => {
        event.preventDefault()
        if (!reference.trim()) return
        recordBooking.mutate(
          { shipmentId, body: { reference: reference.trim() } },
          {
            onSuccess: (data) => {
              toast.success(typeof data?.summary === 'string' ? data.summary : 'Booking saved')
              onDone()
            },
            onError: (error) => toast.error(formatApiErrorMessage('Booking not saved', error)),
          },
        )
      }}
    >
      <div className="min-w-0 flex-1 space-y-1 sm:max-w-xs">
        <Label htmlFor={`${id}-reference`} className="text-xs">
          BOL
        </Label>
        <InlineFieldInput
          id={`${id}-reference`}
          value={reference}
          autoFocus
          onChange={(event) => setReference(event.target.value)}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onDone} disabled={recordBooking.isPending}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          size="sm"
          loading={recordBooking.isPending}
          loadingText="Looking up..."
          disabled={!reference.trim()}
        >
          Save booking
        </LoadingButton>
      </div>
    </form>
  )
}
